import type { ViewProps } from 'react-native';
import { slugify } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useProductDetailsScreenContext } from '../../ProductDetailsScreen.provider';
import type { IngredientModificationWithQuantity } from '../../types';

// ────────────────────────────────────────────────────────────────────────────────

export const useIngredientModificationQty = (
  ingredientModification: IngredientModificationWithQuantity,
) => {
  const { isCustomizationActive, activeIngredientsModificationsMap } =
    useProductDetailsScreenContext();

  const { kind, quantity, ingredient } = ingredientModification;

  const activeIngredientModification = activeIngredientsModificationsMap.get(
    ingredient.id,
  );
  const activeIngredientModificationQty =
    activeIngredientModification?.quantity;
  const qty = activeIngredientModificationQty ?? quantity ?? 0;

  // quantity should not be shown for certain kinds
  const shouldDisplayQtyForKind = kind !== 'dressings';

  const shouldDisplayQtyInIdleMode =
    shouldDisplayQtyForKind && !isCustomizationActive && qty > 1;
  const shouldDisplayQtyInCustomizationMode =
    shouldDisplayQtyForKind && isCustomizationActive;
  const shouldDisplayQty =
    shouldDisplayQtyInIdleMode || shouldDisplayQtyInCustomizationMode;

  return shouldDisplayQty ? qty : 0;
};

// ────────────────────────────────────────────────────────────────────────────────

export const useIngredientModificationA11yLabels = (
  ingredientModification: IngredientModificationWithQuantity,
  ingredientQty: number,
  accessibilityLabel: ViewProps['accessibilityLabel'],
) => {
  const { ingredient } = ingredientModification;
  const { name } = ingredient;
  const { t } = useLocalizationContext();

  const canDecreaseQty = ingredientQty > 1;

  // ────────────────────────────────────────────────────────────────────

  const increaseQtyA11yLabel =
    accessibilityLabel ??
    t('pdp.modifications.increase-ingredient-modification', {
      ingredientModification: name,
    });

  const decreaseQtyA11yLabel =
    accessibilityLabel ??
    t(
      canDecreaseQty
        ? `pdp.modifications.decrease-ingredient-modification`
        : 'pdp.modifications.remove-ingredient-modification',
      { ingredientModification: name },
    );

  return { increaseQtyA11yLabel, decreaseQtyA11yLabel };
};

// ────────────────────────────────────────────────────────────────────────────────

export const useIngredientModificationCardTestID = (
  ingredientModification: IngredientModificationWithQuantity,
  testIdPrefix = 'ingredient-modification-card',
) => {
  const { ingredient } = ingredientModification;
  const { name } = ingredient;
  const ingredientSlug = slugify(name);

  return `${testIdPrefix}-${ingredientSlug}`;
};
